import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Clock, CheckCircle, XCircle, Plus, 
  Search, Filter, Download, Eye, Truck, LogOut,
  Bell, Settings, User, Calendar, DollarSign,
  HandMetal, PackageOpen, Wrench, Ban, HelpCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import NotificationSystem from '../components/NotificationSystem';
import FileUpload from '../components/FileUpload';
import AdvancedFilters from '../components/AdvancedFilters';
import RatingDisplay, { StarRating } from '../components/RatingDisplay';
import RatingForm from '../components/RatingForm';


interface Pedido {
  id: number;
  titulo: string;
  descricao: string;
  categoria: string;
  urgencia: string;
  status: 'pendente' | 'em_andamento' | 'aprovado' | 'reprovado' | 'finalizado' | 'negociando' | 'aguardando_peca' | 'em_execucao' | 'cancelado';
  created_at: string;
  orcamentos_count: number;
  fornecedores_interessados?: string;
  orcamentos?: Array<{
    id: number;
    fornecedor: string;
    valor: number;
    prazo_entrega: string;
    observacoes: string;
    status: string;
  }>;
}

const ClienteDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [selectedFornecedor, setSelectedFornecedor] = useState<string>('');
  const [ratings, setRatings] = useState<any[]>([]);

  useEffect(() => {
    loadPedidos();
    loadRatings();
  }, []);

  const loadPedidos = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch('/api/clientes/pedidos', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const responseData = await response.json();
          console.log('Resposta da API:', responseData);
          
          const pedidosData = responseData.pedidos || [];
          console.log('Pedidos carregados da API:', pedidosData);
          
          // Mapear dados da API para o formato esperado
          const pedidosMapeados = pedidosData.map((pedido: any) => ({
            id: pedido.id,
            titulo: pedido.titulo || `${pedido.tipo_equipamento || 'Equipamento'} - ${pedido.marca || 'Marca'} ${pedido.modelo || 'Modelo'}`,
            categoria: pedido.categoria || pedido.tipo_peca || 'Categoria',
            tipo_equipamento: pedido.tipo_equipamento || 'Equipamento',
            marca: pedido.marca || 'Marca',
            modelo: pedido.modelo || 'Modelo',
            tipo_peca: pedido.tipo_peca || 'Peça',
            descricao: pedido.descricao || pedido.titulo || 'Descrição do pedido',
            status: pedido.status || 'pendente',
            data_criacao: pedido.created_at || new Date().toISOString(),
            created_at: pedido.created_at ? new Date(pedido.created_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR'),
            orcamentos_count: pedido.orcamentos_count || 0,
            valor_estimado: pedido.orcamento_maximo || 0,
            urgencia: pedido.urgencia || 'normal'
          }));
          
          setPedidos(pedidosMapeados);
          return;
        }
      } catch (apiError) {
        console.log('Erro ao buscar da API, usando dados mockados:', apiError);
      }

      // Fallback para dados mockados se a API não estiver disponível
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Usar o mesmo mapeamento das outras páginas
      const orcamentosMapping: { [key: number]: number } = {
        4: 1,  // ID 4 tem 1 orçamento
        7: 2,  // ID 7 tem 2 orçamentos
        10: 1, // ID 10 tem 1 orçamento
        13: 2, // ID 13 tem 2 orçamentos
        16: 1, // ID 16 tem 1 orçamento
        19: 2, // ID 19 tem 2 orçamentos
        22: 1, // ID 22 tem 1 orçamento
        25: 2, // ID 25 tem 2 orçamentos
        28: 1, // ID 28 tem 1 orçamento
        31: 2  // ID 31 tem 2 orçamentos
      };
      
      const mockPedidos = Array.from({ length: 10 }, (_, index) => {
        const pedidoId = index + 1;
        const orcamentosCount = orcamentosMapping[pedidoId] !== undefined ? orcamentosMapping[pedidoId] : pedidoId % 3;
        const statusOptions = ['pendente', 'em_andamento', 'aprovado', 'finalizado', 'cancelado'];
        const status = statusOptions[pedidoId % statusOptions.length];
        
        let statusFinal = status;
        if (orcamentosCount > 0) {
          statusFinal = 'em_andamento';
        } else if (orcamentosCount === 0 && (status === 'em_andamento' || status === 'aprovado' || status === 'finalizado')) {
          statusFinal = 'aguardando_orcamentos';
        }
        
                 return {
           id: pedidoId,
           titulo: `${pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema Hidráulico' : 'Motor'} para ${pedidoId % 3 === 0 ? 'Trator' : pedidoId % 2 === 0 ? 'Pá Carregadeira' : 'Escavadeira'}`,
           categoria: pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Hidráulica' : 'Motor',
           tipo_equipamento: pedidoId % 3 === 0 ? 'Trator' : pedidoId % 2 === 0 ? 'Pá Carregadeira' : 'Escavadeira',
           marca: pedidoId % 3 === 0 ? 'John Deere' : pedidoId % 2 === 0 ? 'Komatsu' : 'Caterpillar',
           modelo: pedidoId % 3 === 0 ? '750J' : pedidoId % 2 === 0 ? 'WA380' : '320D',
           tipo_peca: pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema Hidráulico' : 'Motor',
           descricao: `${pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema hidráulico' : 'Motor'} completo para ${pedidoId % 3 === 0 ? 'trator' : pedidoId % 2 === 0 ? 'pá carregadeira' : 'escavadeira'} ${pedidoId % 3 === 0 ? 'John Deere 750J' : pedidoId % 2 === 0 ? 'Komatsu WA380' : 'Caterpillar 320D'}`,
           status: statusFinal as "pendente" | "em_andamento" | "aprovado" | "reprovado" | "finalizado",
           data_criacao: new Date(Date.now() - (pedidoId * 24 * 60 * 60 * 1000)).toISOString(),
           created_at: new Date(Date.now() - (pedidoId * 24 * 60 * 60 * 1000)).toLocaleDateString('pt-BR'),
           orcamentos_count: orcamentosCount,
           valor_estimado: 15000 + (pedidoId * 500),
           urgencia: pedidoId % 3 === 0 ? 'alta' : pedidoId % 2 === 0 ? 'normal' : 'baixa'
         };
      });
      
      setPedidos(mockPedidos);
      console.log('Pedidos carregados (mockados):', mockPedidos);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRatings = () => {
    const mockRatings = [
      {
        id: 1,
        fornecedor: 'Peças Express Ltda',
        rating: 4.8,
        comentario: 'Excelente qualidade e entrega rápida. Recomendo!',
        data: '2024-01-10'
      },
      {
        id: 2,
        fornecedor: 'Mecânica Industrial Ltda',
        rating: 4.5,
        comentario: 'Bom preço e atendimento profissional',
        data: '2024-01-08'
      }
    ];
    setRatings(mockRatings);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    toast.success('Logout realizado com sucesso');
    navigate('/');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'text-yellow-600';
      case 'em_andamento':
        return 'text-blue-600';
      case 'aprovado':
        return 'text-purple-600';
      case 'reprovado':
        return 'text-red-600';
      case 'finalizado':
        return 'text-green-600';
      case 'negociando':
        return 'text-orange-600';
      case 'aguardando_peca':
        return 'text-indigo-600';
      case 'em_execucao':
        return 'text-cyan-600';
      case 'cancelado':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Package className="w-4 h-4" />;
      case 'em_andamento':
        return <Clock className="w-4 h-4" />;
      case 'aprovado':
        return <CheckCircle className="w-4 h-4" />;
      case 'reprovado':
        return <XCircle className="w-4 h-4" />;
      case 'finalizado':
        return <CheckCircle className="w-4 h-4" />;
      case 'negociando':
        return <HandMetal className="w-4 h-4" />;
      case 'aguardando_peca':
        return <PackageOpen className="w-4 h-4" />;
      case 'em_execucao':
        return <Wrench className="w-4 h-4" />;
      case 'cancelado':
        return <Ban className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'Pendente';
      case 'em_andamento':
        return 'Em Andamento';
      case 'aprovado':
        return 'Aprovado';
      case 'reprovado':
        return 'Reprovado';
      case 'finalizado':
        return 'Finalizado';
      case 'negociando':
        return 'Negociando';
      case 'aguardando_peca':
        return 'Aguardando Peça';
      case 'em_execucao':
        return 'Em Execução';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getUrgenciaColor = (urgencia: string) => {
    switch (urgencia) {
      case 'Alta':
        return 'text-red-600 bg-red-100';
      case 'Média':
        return 'text-yellow-600 bg-yellow-100';
      case 'Baixa':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const maskCompanyName = (companyName: string) => {
    if (!companyName) return '';
    const words = companyName.split(' ');
    return words.map(word => word.charAt(0)).join('.') + '...';
  };

  const filteredPedidos = pedidos.filter(pedido => {
    const matchesSearch = pedido.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pedido.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pedido.categoria.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'todos' || pedido.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleRateFornecedor = (fornecedor: string) => {
    setSelectedFornecedor(fornecedor);
    setShowRatingForm(true);
  };

  const handleSubmitRating = (ratingData: any) => {
    // Aqui você implementaria a lógica para salvar a avaliação
    toast.success('Avaliação enviada com sucesso!');
    setShowRatingForm(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="flex items-center space-x-4">
              <NotificationSystem />
              <button
                onClick={() => navigate('/ajuda')}
                className="btn btn-outline"
                title="Central de Ajuda"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Ajuda
              </button>
              <button 
                onClick={() => navigate('/configuracoes')}
                className="btn btn-outline"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </button>
              <button onClick={handleLogout} className="btn btn-outline">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold dashboard-title mb-2">
            Meus Pedidos
          </h1>
          <p className="dashboard-subtitle">
            Acompanhe seus pedidos e orçamentos recebidos
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Pedidos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.length}
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Orçamentos Recebidos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.reduce((total, pedido) => total + pedido.orcamentos_count, 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pedidos Ativos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.filter(p => 
                    p.status === 'pendente' || 
                    p.status === 'em_andamento' || 
                    p.status === 'aprovado' || 
                    p.status === 'negociando' ||
                    p.status === 'aguardando_peca' ||
                    p.status === 'em_execucao'
                  ).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pedidos Finalizados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.filter(p => p.status === 'finalizado').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card mb-8">
          <h3 className="text-lg font-semibold section-header">Ações Rápidas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/novo-pedido')}
              className="quick-action-card flex items-center"
            >
              <Plus className="w-6 h-6 text-red mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Novo Pedido</p>
                <p className="text-sm text-gray-600">Criar pedido</p>
              </div>
            </button>

            <button
              onClick={() => setFilterStatus('pendente')}
              className="quick-action-card flex items-center"
            >
              <Clock className="w-6 h-6 text-yellow-500 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Pedidos Pendentes</p>
                <p className="text-sm text-gray-600">Ver pendentes</p>
              </div>
            </button>

            <button
              onClick={() => setFilterStatus('finalizado')}
              className="quick-action-card flex items-center"
            >
              <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Pedidos Finalizados</p>
                <p className="text-sm text-gray-600">Ver finalizados</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/configuracoes')}
              className="quick-action-card flex items-center"
            >
              <Settings className="w-6 h-6 text-blue-500 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Configurações</p>
                <p className="text-sm text-gray-600">Ajustar conta</p>
              </div>
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="search-filter-container mb-6">
          <div className="search-input-container">
            <Search className="w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar pedidos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="filter-button-container">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-select"
            >
              <option value="todos">Todos os Status</option>
              <option value="pendente">Pendente</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="aprovado">Aprovado</option>
              <option value="negociando">Negociando</option>
              <option value="aguardando_peca">Aguardando Peça</option>
              <option value="em_execucao">Em Execução</option>
              <option value="finalizado">Finalizado</option>
              <option value="reprovado">Reprovado</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <button
              onClick={() => navigate('/novo-pedido')}
              className="btn btn-primary whitespace-nowrap"
              style={{ minWidth: '200px' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Pedido
            </button>
          </div>
        </div>

        {/* Pedidos List */}
        <div className="space-y-4">
          {filteredPedidos.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || filterStatus !== 'todos' ? 'Nenhum pedido encontrado' : 'Nenhum pedido ainda'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || filterStatus !== 'todos' 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Faça seu primeiro pedido e comece a receber orçamentos'
                }
              </p>
              {!searchTerm && filterStatus === 'todos' && (
                <button
                  onClick={() => navigate('/novo-pedido')}
                  className="btn btn-primary"
                  style={{ minWidth: '200px' }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Fazer Primeiro Pedido
                </button>
              )}
            </div>
          ) : (
            filteredPedidos.map((pedido) => (
              <div key={pedido.id} className="card">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {pedido.titulo}
                        </h3>
                        <p className="text-gray-600 mb-2">
                          {pedido.descricao}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Categoria: {pedido.categoria}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgenciaColor(pedido.urgencia)}`}>
                            {pedido.urgencia}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pedido.status)}`}>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(pedido.status)}
                            <span>{getStatusText(pedido.status)}</span>
                          </div>
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4 sm:mb-0">
                        <span>Criado em: {pedido.created_at}</span>
                        <span>Orçamentos: {pedido.orcamentos_count}</span>
                        {pedido.fornecedores_interessados && (
                          <span>Fornecedores: {pedido.fornecedores_interessados}</span>
                        )}
                      </div>
                    </div>

                    {/* Botões em linha separada com largura total */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                      <button 
                        onClick={() => navigate(`/cliente/pedidos/${pedido.id}`)}
                        className="btn btn-outline flex-1"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Ver Detalhes
                      </button>
                      {pedido.orcamentos_count > 0 && (
                        <button 
                          onClick={() => navigate(`/cliente/orcamentos/${pedido.id}`)}
                          className="btn btn-primary flex-1"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Ver Orçamentos
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recent Ratings */}
        {ratings.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Avaliações Recentes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ratings.map((rating) => (
                <div key={rating.id} className="card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{rating.fornecedor}</h4>
                                             <div className="flex items-center mt-1">
                         <StarRating rating={rating.rating} />
                         <span className="text-sm text-gray-500 ml-2">{rating.rating}/5</span>
                       </div>
                      <p className="text-sm text-gray-600 mt-2">{rating.comentario}</p>
                      <p className="text-xs text-gray-500 mt-2">{rating.data}</p>
                    </div>
                    <button
                      onClick={() => handleRateFornecedor(rating.fornecedor)}
                      className="btn btn-outline text-xs"
                    >
                      Avaliar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rating Form Modal */}
      {showRatingForm && (
        <RatingForm
          fornecedor={selectedFornecedor}
          onSubmit={handleSubmitRating}
          onClose={() => setShowRatingForm(false)}
        />
      )}

    </div>
  );
};

export default ClienteDashboard;